/**
 * Lightweight JWT payload decoder.
 *
 * We do NOT verify the signature here — Keycloak validates tokens server-side.
 * This is purely a client-side claims extractor that must never throw, since
 * a malformed token can come from a stale cache, manual edit, or partial
 * write to localStorage; throwing would crash the auth flow and white-screen
 * the app.
 */

export interface JwtPayload {
  sub?: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  exp?: number;
  iat?: number;
  groups?: string[];
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] } | undefined>;
  [key: string]: unknown;
}

/**
 * Decode the payload of a JWT. Returns null when the token is missing,
 * malformed, or the payload is not valid JSON. Never throws.
 */
export function decodeJwtPayload(token: string | null | undefined): JwtPayload | null {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    // base64url → base64: replace URL-safe chars and pad to length % 4 = 0.
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const json = atob(padded);
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as JwtPayload;
  } catch {
    return null;
  }
}
